import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import Disclaimer from "@/components/Disclaimer";
import { api } from "@/lib/api";
import { calculateProjectedScore, scoreToColor, scoreToGrade } from "@/lib/scoring";
import { Lock, AlertCircle, AlertTriangle, Info, ExternalLink, ArrowRight, RefreshCw, Flag } from "lucide-react";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";

const PRIORITY_META = {
  CRITICAL: { color: "var(--rg-red)", Icon: AlertCircle, order: 0 },
  HIGH: { color: "var(--rg-amber)", Icon: AlertTriangle, order: 1 },
  MEDIUM: { color: "var(--rg-sage)", Icon: Info, order: 2 },
  LOW: { color: "var(--rg-muted)", Icon: Info, order: 3 },
};

export default function Remediation() {
  const [report, setReport] = useState(null);
  const [tier, setTier] = useState("free");
  const [checked, setChecked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flaggedActions, setFlaggedActions] = useState({}); // action text → status
  const [flagOpenId, setFlagOpenId] = useState(null);
  const [flagReason, setFlagReason] = useState("");

  // Mount-only fetch.
  useEffect(() => {
    api.get("/audit/me/report").then(({ data }) => {
      setReport(data.report);
      setTier(data.tier || "free");
    }).finally(() => setLoading(false));
    api.get("/audit/me/disputes").then(({ data }) => {
      const m = {};
      (data.disputes || []).forEach((d) => {
        if (d.recommendation_action) m[d.recommendation_action] = d.status;
      });
      setFlaggedActions(m);
    }).catch(() => {});
  }, []);

  const items = useMemo(() => {
    const arr = (report?.remediationItems || report?.readinessRecommendations || []).map((it, i) => ({ ...it, id: `${it.action}-${i}` }));
    return arr.sort((a, b) => (PRIORITY_META[a.priority]?.order ?? 9) - (PRIORITY_META[b.priority]?.order ?? 9));
  }, [report]);

  const submitFlag = async (item) => {
    try {
      await api.post("/audit/dispute-finding", {
        finding_text: item.action,
        dispute_reason: flagReason,
        jurisdiction: item.jurisdiction || "",
        recommendation_action: item.action,
      });
      setFlaggedActions((s) => ({ ...s, [item.action]: "open" }));
      toast.success("Flagged for review. Our team will investigate.");
      setFlagOpenId(null);
      setFlagReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to flag finding");
    }
  };

  const projected = useMemo(() => {
    if (!report) return 0;
    return calculateProjectedScore(report.composite_score || 0, items, checked);
  }, [report, items, checked]);

  const progress = items.length ? (checked.length / items.length) * 100 : 0;

  if (loading) return <Layout><div className="max-w-7xl mx-auto px-6 py-20" /></Layout>;

  if (!report) {
    return (
      <Layout>
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h1 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-3">No audit yet</h1>
          <Link to="/onboarding"><Button className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold">Run Audit</Button></Link>
        </section>
      </Layout>
    );
  }

  if (report.is_locked_pro) {
    return (
      <Layout>
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Lock className="w-12 h-12 text-[var(--rg-sage)] mx-auto mb-3" />
          <h1 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-2">Remediation is a Pro feature</h1>
          <p className="text-[var(--rg-text)] mb-5">Pro unlocks your full remediation plan with projected score calculator.</p>
          <Link to="/billing"><Button size="lg" className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold">Upgrade to Pro</Button></Link>
        </section>
      </Layout>
    );
  }

  const projectedColor = scoreToColor(projected);

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" offset={80} />
      <section className="max-w-5xl mx-auto px-6 py-10">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Readiness Recommendations</div>
        <h1 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-[var(--rg-text-strong)] mb-2">Readiness Recommendations</h1>
        <p className="text-[var(--rg-text)] mb-8 max-w-2xl">Check off items you&apos;ve reviewed or completed to see your projected score update in real time. Click &quot;Run Updated Audit&quot; when ready to confirm.</p>

        {/* Projected score card */}
        <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 grid sm:grid-cols-3 gap-6 items-center mb-8" data-testid="projected-score-card">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-muted)]">Current</div>
            <div className="font-heading font-extrabold text-5xl mt-1" style={{ color: scoreToColor(report.composite_score) }}>{report.composite_score}</div>
            <div className="text-xs text-[var(--rg-text)] font-mono mt-1">{scoreToGrade(report.composite_score)}</div>
          </div>
          <div className="text-center">
            <ArrowRight className="w-8 h-8 mx-auto text-[var(--rg-sage)]" />
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--rg-muted)] mt-2 font-bold">{checked.length} of {items.length} done</div>
            <div className="mt-3 h-2 bg-[var(--rg-surface)] border border-[var(--rg-border)] rounded-sm overflow-hidden">
              <motion.div className="h-full bg-[var(--rg-sage)]" initial={{ width: 0 }} animate={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-muted)]">Projected</div>
            <div className="font-heading font-extrabold text-5xl mt-1" style={{ color: projectedColor }} data-testid="projected-score">{projected}</div>
            <div className="text-xs text-[var(--rg-text)] font-mono mt-1">{scoreToGrade(projected)}</div>
          </div>
        </div>

        {checked.length > 0 && (
          <Link to="/onboarding" data-testid="rerun-audit-cta">
            <Button className="mb-6 bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold">
              <RefreshCw className="w-4 h-4 mr-2" /> Run Updated Audit to confirm your new score
            </Button>
          </Link>
        )}

        <div className="space-y-3">
          {items.length === 0 && (
            <div className="rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-6 text-center text-[var(--rg-text-strong)]">
              No readiness recommendations — your readiness looks strong.
            </div>
          )}
          {items.map((item, i) => {
            const meta = PRIORITY_META[item.priority] || PRIORITY_META.LOW;
            const isChecked = checked.includes(item.id);
            const disputeStatus = flaggedActions[item.action]; // open | confirmed_correct | marked_corrected
            const underReview = disputeStatus === "open";
            const isOpenFlag = flagOpenId === item.id;
            return (
              <div key={item.id} className={`relative rounded-md border p-5 transition-fast ${
                underReview ? "border-l-4 border-l-[var(--rg-amber)] border-[var(--rg-border)] bg-[var(--rg-amber-soft)]" :
                isChecked ? "border-[var(--rg-sage)] bg-[var(--rg-sage)]/5" : "border-[var(--rg-border)] bg-[var(--rg-surface)] hover:border-[var(--rg-border-hi)]"
              }`} data-testid={`remediation-item-${i}`}>
                {underReview && (
                  <div className="mb-2 flex items-center gap-2" data-testid={`under-review-badge-${i}`}>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider text-[var(--rg-amber)] border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)]">
                      <Flag className="w-3 h-3" /> Under Review
                    </span>
                    <span className="text-xs text-[var(--rg-text)] italic">
                      This finding has been flagged for review. A provisional score is shown while our team investigates.
                    </span>
                  </div>
                )}
                <label className="flex flex-col sm:flex-row sm:items-start gap-4 cursor-pointer">
                  <Checkbox checked={isChecked} onCheckedChange={() => setChecked(s => isChecked ? s.filter(x => x !== item.id) : [...s, item.id])} className="border-[var(--rg-border-hi)] mt-1" />
                  <div className="flex items-center gap-2 px-2.5 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wide self-start" style={{ color: meta.color, borderColor: meta.color, borderWidth: 1, borderStyle: "solid", backgroundColor: `${meta.color}1a` }}>
                    <meta.Icon className="w-3.5 h-3.5" /> {item.priority}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[var(--rg-text-strong)] font-semibold break-words ${isChecked ? "line-through opacity-70" : ""}`}>{item.action}</div>
                    <div className="text-xs text-[var(--rg-muted)] font-mono mt-1 break-all">
                      {item.jurisdiction} · {item.statuteCitation}
                      {item.officialURL && <a href={item.officialURL} target="_blank" rel="noreferrer" className="text-[var(--rg-sage)] hover:underline ml-2 inline-flex items-center gap-1">official source <ExternalLink className="w-3 h-3" /></a>}
                    </div>
                  </div>
                  <div className="text-[var(--rg-sage)] font-mono font-bold text-lg self-start">+{item.potentialGain}</div>
                </label>

                {/* Flag micro-button (bottom-right). Hidden when already flagged/under-review. */}
                {!underReview && (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => { setFlagOpenId(isOpenFlag ? null : item.id); setFlagReason(""); }}
                      className="text-[11px] text-[var(--rg-muted)] hover:text-[var(--rg-amber)] inline-flex items-center gap-1 underline decoration-dotted underline-offset-2"
                      data-testid={`flag-btn-${i}`}
                    >
                      <Flag className="w-3 h-3" /> Flag as Inaccurate
                    </button>
                  </div>
                )}

                {/* Inline flag form */}
                {isOpenFlag && !underReview && (
                  <div className="mt-3 rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-3 space-y-2" data-testid={`flag-form-${i}`}>
                    <div className="text-xs text-[var(--rg-amber)] font-bold uppercase tracking-wider">Why is this finding inaccurate? (optional)</div>
                    <Textarea
                      value={flagReason}
                      onChange={(e) => setFlagReason(e.target.value)}
                      rows={3}
                      placeholder="e.g., We're not actually selling to NY residents at the moment…"
                      className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] text-sm"
                      data-testid={`flag-reason-${i}`}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => { setFlagOpenId(null); setFlagReason(""); }} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)] text-xs h-8" data-testid={`flag-cancel-${i}`}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={() => submitFlag(item)} className="bg-[var(--rg-amber)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-amber)] font-semibold text-xs h-8" data-testid={`flag-submit-${i}`}>
                        Submit Flag
                      </Button>
                    </div>
                  </div>
                )}

                {/* Resolved indicator */}
                {disputeStatus && disputeStatus !== "open" && (
                  <div className="mt-3 text-[11px] text-[var(--rg-muted)]">
                    {disputeStatus === "confirmed_correct" && <span>Reviewed by Regulens — finding confirmed as accurate.</span>}
                    {disputeStatus === "marked_corrected" && <span className="text-[var(--rg-sage)]">Reviewed by Regulens — corrected.</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8"><Disclaimer /></div>
      </section>
    </Layout>
  );
}
